import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { finalize, map, startWith } from 'rxjs/operators';
import { AdminUtilsService } from '../../service/admin-utils.service';
import { UserProfile, ManageAccessPopupData, LocationAccessEmailDomain, PatchLocationDomainsRequest } from '../../types';
import { UserAdminService } from '../../service/user-admin.service';
import { CustomerPortalService } from '../../service/customer-portal.service';
import { tryGetErrorMessage } from '../../error.utils';

@Component({
  templateUrl: './manage-access-popup.component.html',
  styleUrls: ['./manage-access-popup.component.scss'],
  standalone: false,
})
export class ManageAccessPopupComponent implements OnInit {
  private customerPortalService = inject(CustomerPortalService);
  adminUtilService = inject(AdminUtilsService);

  private userAdminService = inject(UserAdminService);

  branchId: number;
  branchName: string;
  locationName: string;
  location_id: number;

  userForm = new UntypedFormGroup({
    email: new UntypedFormControl('', [
      // This doesn't seem to work at all
      Validators.email,
    ]),
  });

  domain = '';
  domains: LocationAccessEmailDomain[] = [];
  domainRegex = '^(?:\\*\\.)?[a-z0-9]+(?:[\\-.][a-z0-9]+)*\\.[a-z]{2,6}$';
  domainForm = new UntypedFormGroup({ domain: new UntypedFormControl('', [Validators.pattern(this.domainRegex)]) });
  public domainFormMessage = signal<string>('');

  filteredOptions: Observable<string[]>;
  displayedColumns: string[] = ['user-email', 'action'];
  public isRequestSending = signal<boolean>(false);
  selectedUserEmail: string;
  public message = signal<string>('');

  // Disables "Add User" if true
  public isEmailInputInvalid = signal<boolean>(true);

  // Disables "Add Domain" if true
  public isDomainInputInvalid = signal<boolean>(true);

  public isLoading = signal<boolean>(false);

  public allUsers = signal<UserProfile[]>([]);

  public loginUsers = computed(() => {
    return this.allUsers();
  });

  constructor() {
    const data = inject<ManageAccessPopupData>(MAT_DIALOG_DATA);

    this.branchId = data.branchId;
    this.branchName = data.branchName;
    this.locationName = data.locationName;
    this.location_id = data.location_id;
    this.isLoading.set(true);
    this.refreshPermissions();
    this.refreshDomains();
  }

  refreshPermissions() {
    this.userAdminService.getUsersForLocation(this.location_id).subscribe(
      (usersData) => {
        this.allUsers.set(usersData.items);
      },
      () => {},
      () => this.isLoading.set(false),
    );
  }

  refreshDomains() {
    this.customerPortalService.getLocationDomains(this.location_id).subscribe(
      (response) => {
        this.domains = response;
      },
      () => {},
    );
  }

  ngOnInit() {
    const emailField = this.userForm.get('email');
    emailField.valueChanges.subscribe((email: string) => {
      this.isEmailInputInvalid.set(true);
      if (!email) {
        this.message.set('');
        return;
      }
      // Email validation check (@angular/forms validation pattern doesn't seem to work either, rely on our admin-utils)
      if (emailField.hasError('email') || !this.adminUtilService.isValidEmail(email)) {
        this.message.set('Please enter a valid email address');
        return;
      }
      // Existing (case-insensitive) email that already have permissions to this location
      if (this.allUsers() && this.allUsers().some((u) => u.email.toLowerCase() === email.toLowerCase())) {
        this.message.set(`The user with email: ${email.toLowerCase()} already exists on this location`);
        return;
      } else {
        this.message.set('');
        this.isEmailInputInvalid.set(false);
      }
    });

    const domainField = this.domainForm.get('domain');
    domainField.valueChanges.subscribe((domain) => {
      this.isDomainInputInvalid.set(true);
      if (!domain) {
        this.domainFormMessage.set('');
        return;
      }
      // Domain validation check
      if (domainField.hasError('domain') || (domainField.errors && 'pattern' in domainField.errors)) {
        this.domainFormMessage.set('Please enter a valid domain');
        return;
      }

      if (this.domains?.map((d) => d.domain).includes(domain)) {
        this.domainFormMessage.set(`The domain: ${domain} already exists on this location`);
        return;
      }

      this.domainFormMessage.set('');
      this.isDomainInputInvalid.set(false);
    });

    this.filteredOptions = this.userForm.valueChanges.pipe(
      startWith({ email: '' }),
      map((value) => this._filter(value.email)),
    );
  }

  addUser() {
    this.isRequestSending.set(true);
    this.selectedUserEmail = this.selectedUserEmail ? this.selectedUserEmail : this.userForm.get('email').value;
    this.selectedUserEmail = this.selectedUserEmail.toLowerCase().trim();

    this.message.set(`Adding user is in progress, please wait...`);
    if (!this.selectedUserEmail) {
      this.isRequestSending.set(false);
      this.message.set(`Please provide valid email or choose existing email option`);
      return;
    }

    this.userAdminService.createUser(this.selectedUserEmail, this.selectedUserEmail).subscribe({
      next: (user) => {
        this.userAdminService
          .grantLocationRole(user.user_id, this.location_id.toString(), 'guest')
          .pipe(
            finalize(() => {
              this.selectedUserEmail = '';
              this.isRequestSending.set(false);
            }),
          )
          .subscribe({
            next: () => {
              this.refreshPermissions();
              this.userForm.get('email').reset();
              this.userForm.reset();
              this.userForm.get('email').setValue('');
            },
            error: (err) => this.message.set(`Adding user didn't work: ${tryGetErrorMessage(err)}`),
          });
      },
      error: (err) => this.message.set(tryGetErrorMessage(err)),
    });
  }

  addDomain(): void {
    this.isRequestSending.set(true);
    this.domain = this.domainForm.get('domain').value;
    this.domain = this.domain.toLowerCase().trim();

    this.domainFormMessage.set(`Adding domain in progress, please wait...`);
    if (!this.domain) {
      this.isRequestSending.set(false);
      this.message.set(`Please enter a valid domain`);
      return;
    }
    const addRequest: PatchLocationDomainsRequest = {
      domains: [this.domain],
      location_id: this.location_id,
    };
    this.customerPortalService.addLocationDomain(addRequest).subscribe(
      (response: { success: boolean; message: string }) => {
        if (!response.success) {
          this.isRequestSending.set(false);
          this.message.set(`Adding domain didn't work: ${response.message}`);
          return;
        }
        this.domainForm.get('domain').reset();
        this.domainForm.reset();
        this.domainForm.get('domain').setValue('');
        this.isRequestSending.set(false);
        this.domainFormMessage.set(response.message ?? '');
        this.refreshDomains();
      },
      (error) => {
        this.isRequestSending.set(false);
        this.domainFormMessage.set(`Adding domain didn't work: ${JSON.stringify(error.error)}`);
      },
    );
  }

  removeDomain(domain: LocationAccessEmailDomain) {
    this.isRequestSending.set(true);
    this.domainFormMessage.set(`Deleting domain from the list, please wait...`);

    const req: PatchLocationDomainsRequest = {
      domains: [domain.domain],
      location_id: this.location_id,
    };
    this.customerPortalService.removeLocationDomain(req).subscribe(
      () => {
        this.domains = this.domains.filter((d) => !req.domains.includes(d.domain));
        this.isRequestSending.set(false);
        this.domainFormMessage.set('');
      },
      (error) => {
        this.isRequestSending.set(false);
        this.domainFormMessage.set(`Deleting domain from the list didn't work: ${JSON.stringify(error.error)}`);
      },
    );
  }

  handler(event: MatAutocompleteSelectedEvent): void {
    this.userForm.get('email').setValue(event.option.value);
    this.selectedUserEmail = event.option.value;
  }

  removeUser(user: UserProfile) {
    this.isRequestSending.set(true);
    this.message.set(`Deleting user from the list, please wait...`);

    if (!user.email) {
      this.isRequestSending.set(false);
      this.message.set(`Deleting didn't work: missing email`);
      return;
    }
    this.userAdminService.revokeLocationRole(user.user_id, this.location_id.toString(), 'guest').subscribe(
      () => {
        this.refreshPermissions();
        this.isRequestSending.set(false);
        this.message.set(``);
      },
      (error) => {
        // this.error = error.error;
        this.isRequestSending.set(false);
        this.message.set(`Deleting user from the list didn't work: ${JSON.stringify(error.error)}`);
      },
    );
  }

  clearEmail() {
    this.userForm.get('email').setValue('');
    this.selectedUserEmail = '';
    this.userForm.get('email').reset();
    this.message.set(``);
  }

  private _filter(value: string): string[] {
    const filterValue = value?.toString().toLowerCase() ?? '';
    return this.loginUsers()
      .map((u) => u.email)
      .filter((option) => option.toLowerCase().includes(filterValue))
      .sort();
  }
}
